import { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "./firebase";
import { X, Calendar } from "lucide-react";
import { createPortal } from "react-dom";

const TYPE_COLORS = {
  event:        { bg:"#3b82f6", label:"Event" },
  announcement: { bg:"#f59e0b", label:"Announcement" },
  drive:        { bg:"#22c55e", label:"Drive" },
  camp:         { bg:"#a855f7", label:"Camp" },
  urgent:       { bg:"#ef4444", label:"🚨 Urgent" },
};

export default function AnnouncementsBanner() {
  const [posters,   setPosters]   = useState([]);
  const [selected,  setSelected]  = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        setLoadError(false);
        const snap = await getDocs(
          query(collection(db, "announcements"), orderBy("createdAt", "desc"))
        );
        setPosters(
          snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(d => d.active !== false)
        );
      } catch(e) {
        console.error("Announcements load error:", e);
        setLoadError(true);
      }
      finally { setLoading(false); }
    }
    load();
  }, []);

  if (loading) return null;

  if (loadError) {
    return (
      <div className="ann-section">
        <div className="ann-inner" style={{ textAlign:"center", padding:"20px 0" }}>
          <p style={{ color:"#78716c", fontSize:13, marginBottom:10 }}>
            Couldn't load announcements right now.
          </p>
          <button onClick={() => window.location.reload()}
            style={{ background:"#f59e0b", color:"#0a0908", border:"none", borderRadius:50, padding:"10px 20px", fontWeight:800, fontSize:13, cursor:"pointer" }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (posters.length === 0) return null;

  return (
    <>
      <style>{`
        .ann-section {
          background: #0d0c0a;
          border-top: 1px solid rgba(255,255,255,0.05);
          border-bottom: 1px solid rgba(255,255,255,0.05);
          padding: 64px 24px;
        }
        .ann-inner { max-width: 1280px; margin: 0 auto; }
        .ann-label-row { display:flex; align-items:center; gap:10px; margin-bottom:10px; }
        .ann-label-line { width:28px; height:2px; background:#f59e0b; border-radius:1px; flex-shrink:0; }
        .ann-label-text { font-size:12px; font-weight:800; color:#f59e0b; text-transform:uppercase; letter-spacing:2px; }
        .ann-title {
          font-family:'Playfair Display',serif;
          font-size:clamp(24px,4vw,42px);
          font-weight:800; color:#fef3e2; margin:0 0 36px;
        }
        .ann-grid {
          display:grid;
          grid-template-columns:repeat(auto-fill, minmax(260px,1fr));
          gap:16px;
        }
        .ann-card {
          background:#111009;
          border:1px solid rgba(255,255,255,0.07);
          border-radius:14px;
          overflow:hidden;
          cursor:pointer;
          transition:transform 0.2s, border-color 0.2s;
          display:flex;
          flex-direction:column;
        }
        .ann-card:hover { transform:translateY(-4px); border-color:rgba(255,255,255,0.15); }
        .ann-img-wrap {
          position:relative;
          width:100%;
          /* Fixed height for consistent display */
          height:200px;
          overflow:hidden;
          background:#1a1714;
          flex-shrink:0;
        }
        .ann-img {
          width:100%;
          height:100%;
          object-fit:cover;
          object-position:center;
          transition:transform 0.35s;
          display:block;
        }
        .ann-card:hover .ann-img { transform:scale(1.06); }
        .ann-type-badge {
          position:absolute; top:10px; left:10px;
          color:#fff; font-size:10px; font-weight:800;
          padding:3px 10px; border-radius:50px;
          text-transform:uppercase; letter-spacing:0.5px;
        }
        .ann-body { padding:14px 16px 18px; flex:1; }
        .ann-card-title { font-size:15px; font-weight:700; color:#fef3e2; margin:0 0 8px; line-height:1.4; }
        .ann-date { display:flex; align-items:center; gap:5px; font-size:12px; color:#78716c; margin-bottom:6px; }
        .ann-desc { font-size:12px; color:#78716c; line-height:1.6; margin:0 0 10px; overflow:hidden; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; }
        .ann-cta { font-size:12px; color:#f59e0b; font-weight:700; }

        /* ── MOBILE ── */
        @media(max-width:768px) {
          .ann-section { padding:40px 16px; }
          .ann-grid { grid-template-columns:1fr; gap:12px; }
          .ann-img-wrap { height:220px; }
          .ann-title { margin-bottom:24px; }
        }
        @media(max-width:480px) {
          .ann-grid { grid-template-columns:1fr; }
          .ann-img-wrap { height:200px; }
        }

        /* ── LIGHTBOX ── */
        .ann-overlay {
          position:fixed; inset:0; background:rgba(0,0,0,0.92);
          z-index:9999; display:flex; align-items:center; justify-content:center;
          padding:20px; backdrop-filter:blur(12px);
        }
        .ann-modal {
          background:#111009; border:1px solid rgba(255,255,255,0.1);
          border-radius:20px; padding:24px; width:100%; max-width:600px;
          position:relative; max-height:92vh; overflow-y:auto;
        }
        .ann-modal-img {
          width:100%; max-height:60vh; object-fit:contain;
          border-radius:10px; margin-bottom:16px; display:block;
        }
        .ann-close-btn {
          position:absolute; top:14px; right:14px;
          background:rgba(255,255,255,0.08); border:1px solid rgba(255,255,255,0.1);
          border-radius:50%; width:34px; height:34px;
          display:flex; align-items:center; justify-content:center;
          cursor:pointer; color:#a8a29e; z-index:1;
        }
        .ann-close-btn:hover { background:rgba(239,68,68,0.2); color:#ef4444; }
        .ann-modal-badge {
          color:#fff; font-size:10px; font-weight:800;
          padding:3px 10px; border-radius:50px;
          text-transform:uppercase; letter-spacing:0.5px;
          display:inline-block; margin-bottom:10px;
        }
        .ann-modal-title {
          font-family:'Playfair Display',serif;
          font-size:20px; font-weight:800; color:#fef3e2; margin:0 0 8px;
        }
        .ann-modal-date { display:flex; align-items:center; gap:4px; font-size:12px; color:#78716c; margin-bottom:8px; }
        .ann-modal-desc { font-size:14px; color:#a8a29e; line-height:1.7; margin:0; }

        @media(max-width:480px) {
          .ann-modal { padding:16px; border-radius:14px; }
          .ann-modal-img { max-height:50vh; }
          .ann-modal-title { font-size:17px; }
        }
      `}</style>

      <div className="ann-section">
        <div className="ann-inner">
          {/* Header */}
          <div className="ann-label-row">
            <span className="ann-label-line"/>
            <span className="ann-label-text">Latest Updates</span>
          </div>
          <h2 className="ann-title">Events &amp; Announcements</h2>

          {/* Grid */}
          <div className="ann-grid">
            {posters.map(poster => {
              const tc = TYPE_COLORS[poster.type] || TYPE_COLORS.event;
              return (
                <div key={poster.id} className="ann-card" onClick={() => setSelected(poster)}>
                  <div className="ann-img-wrap">
                    <img
                      src={poster.imageUrl}
                      alt={poster.title}
                      className="ann-img"
                      loading="lazy"
                      onError={e => { e.target.style.display="none"; }}
                    />
                    <div className="ann-type-badge" style={{ background: tc.bg }}>
                      {tc.label}
                    </div>
                  </div>
                  <div className="ann-body">
                    <h3 className="ann-card-title">{poster.title}</h3>
                    {poster.date && (
                      <div className="ann-date">
                        <Calendar size={12} color="#f59e0b"/>{poster.date}
                      </div>
                    )}
                    {poster.description && (
                      <p className="ann-desc">{poster.description}</p>
                    )}
                    <div className="ann-cta">View Full Poster →</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selected && createPortal(
  <div className="ann-overlay" onClick={() => setSelected(null)}>
          <div className="ann-modal" onClick={e => e.stopPropagation()}>
            <button className="ann-close-btn" onClick={() => setSelected(null)}>
              <X size={16}/>
            </button>
            <img
              src={selected.imageUrl}
              alt={selected.title}
              className="ann-modal-img"
            />
            <div
              className="ann-modal-badge"
              style={{ background: (TYPE_COLORS[selected.type]||TYPE_COLORS.event).bg }}>
              {(TYPE_COLORS[selected.type]||TYPE_COLORS.event).label}
            </div>
            {selected.date && (
              <div className="ann-modal-date">
                <Calendar size={12} color="#f59e0b"/>{selected.date}
              </div>
            )}
            <h3 className="ann-modal-title">{selected.title}</h3>
            {selected.description && (
              <p className="ann-modal-desc">{selected.description}</p>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}